import {
  Box,
  Button,
  Center,
  Flex,
  Heading,
  Icon,
  Modal,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Text,
  useColorModeValue,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { AlertCircle, Copy } from 'lucide-react';
import { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import iflexApi from '../../services/iflexApi';
import { abrirBoleto } from '../../utils/abrirBoleto';
import formatValue from '../../utils/formatValue';
import getBuildFormUrlencodedParams from '../../utils/getBuildFormUrlencodedParams';
import { Select } from '../shared/Select';
import { Input } from '../shared/Input';
import { formatCustomDate } from '../../utils/formatCustomDate';
// import { IInvoicesData } from '../../services/hooks/useInvoices';

interface IInvoiceModal {
  onClose: any;
  isOpen: any;
  size: string;
  documento: any;
  // documento: IInvoicesData['rows'][number] | any;
  vencimentoMaisAntigo: any;
}

const InvoiceModal: React.FC<IInvoiceModal> = ({
  onClose,
  isOpen,
  size,
  documento,
  vencimentoMaisAntigo,
}) => {
  const toast = useToast();
  const dataHoje = new Date().toLocaleString();

  // estados
  const [barcode, setBarcode] = useState('');
  const [gerando, setGerando] = useState(false);
  const [formaPagamento, setFormaPagamento] = useState('Boleto');
  const [showQr, setShowQr] = useState(true);
  const dataHojeFormatada = formatCustomDate(
    dataHoje,
    'dd/MM/yyyy, HH:mm:ss',
    'yyyy-MM-dd',
  );
  const [novoVencimento, setNovoVencimento] = useState(dataHojeFormatada);

  // estilos
  const color = useColorModeValue('gray.700', 'white');
  const bg = useColorModeValue('white', 'gray.800');
  const warningColor = useColorModeValue('red.700', 'red.200');

  /*
   * copiar código do qrcode da fatura
   */
  const copyBarcode = () => {
    navigator.clipboard.writeText(barcode);
    toast({
      title: 'Código Copiado',
      status: 'info',
      isClosable: true,
      duration: 1000,
      position: 'bottom-right',
    });
  };

  const BarCodeModal = () => {
    return (
      <Modal onClose={() => setShowQr(false)} isOpen={showQr}>
        <ModalOverlay />
        <ModalContent borderWidth="1px" rounded="2xl" p="4" w="100%">
          <ModalCloseButton onClick={() => setShowQr(false)} />
          <Flex p="2" w="100%" align="center" justify="center" flexDir="column">
            <Flex p="1" borderWidth="2px" rounded="5px">
              <QRCodeSVG value={barcode} size={250} />
            </Flex>
            <Box w="100%" p="1" borderWidth="2px" mt="2" rounded="2xl">
              <Text color={color}>{barcode}</Text>
            </Box>
            <Button
              mt="2"
              onClick={() => copyBarcode()}
              leftIcon={<Copy />}
              variant="outline"
              borderWidth="2px"
            >
              Copiar
            </Button>
          </Flex>
        </ModalContent>
      </Modal>
    );
  };

  /*
   * Gera boleto no sistema
   */
  const pegaBoleto = async () => {
    try {
      const data = getBuildFormUrlencodedParams({
        action: 'geraBoleto',
        controller: 'Fatura',
        id_fin: documento?.id_fin,
        id_blq: documento?.id_blq,
        documento_fin: documento?.documento_fin,
        vencimento_fin: documento?.vencimento_fin,
        boleto_fin: documento?.boleto_fin,
        empresa_emp: documento?.empresa_emp,
        novovencimento_fin: formatCustomDate(
          novoVencimento,
          'yyyy-MM-dd',
          "yyyy-MM-dd'T'HH:mm:ss",
        ),
      });

      const response = await iflexApi.post('', data);
      if (response?.data?.success) {
        return response.data.file;
      }
      toast({
        title: response?.data?.msg,
        status: 'error',
        isClosable: true,
        position: 'bottom-right',
      });
      return 'erro';
    } catch (error) {
      return 'erro';
    }
  };

  /*
   * Gera pix no sistema
   */
  const pegaQrCode = async () => {
    try {
      const data = getBuildFormUrlencodedParams({
        action: 'gerarQrCode',
        controller: 'Fatura',
        id_fin: documento?.id_fin,
        empresa_emp: documento?.empresa_emp,
      });
      const response = await iflexApi.post('', data);
      if (response.data.success === true) {
        return response?.data?.QrCode;
      }
      toast({
        title: response?.data?.msg,
        status: 'error',
        isClosable: true,
        position: 'bottom-right',
      });
      return 'erro';
    } catch (error) {
      return 'erro';
    }
  };

  /*
   * gera cobrança
   */
  const gerarCobranca = async () => {
    return;
    if (gerando) return;
    setGerando(true);
    try {
      if (formaPagamento === 'Pix') {
        const qrCode = await pegaQrCode();
        if (qrCode !== 'erro') {
          setBarcode(qrCode);
          setShowQr(true);
        }
      } else if (formaPagamento === 'Boleto') {
        const boleto = await pegaBoleto();
        if (boleto !== 'erro') {
          abrirBoleto(boleto);
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setGerando(false);
    }
  };

  if (documento?.documento_fin !== vencimentoMaisAntigo) {
    return (
      <Modal onClose={onClose} isOpen={isOpen} size={size}>
        <ModalOverlay />
        <ModalContent borderWidth="1px" w="40%" rounded="2xl" bg={bg}>
          <Flex w="100%" h="100%" p="4" flexDir="column">
            <Heading fontWeight="medium" fontSize="2xl">
              Fatura - {documento?.documento_fin}
            </Heading>
            <Flex pt="4">
              <Text color="blue.300">
                Existe Fatura(s) com Vencimento anterior ao selecionado. Realize
                o pagamento da(s) parcelas mais antigas primeiro.
              </Text>
            </Flex>
          </Flex>
        </ModalContent>
      </Modal>
    );
  }

  return (
    <Modal onClose={onClose} isOpen={isOpen} size={size}>
      <ModalOverlay />
      <ModalContent borderWidth="1px" w="40%" rounded="2xl" bg={bg}>
        <Flex w="100%" h="100%" p="4" flexDir="column">
          <Heading fontWeight="medium" fontSize="2xl">
            Fatura - {documento?.documento_fin}
          </Heading>
          <ModalCloseButton onClick={() => onClose()} />
          <Flex direction="row" w="100%" mt="4">
            <Flex w="50%" justify="space-between">
              <VStack align="start" color={color}>
                <Text>Vencimento</Text>
                <Text>Valor Bruto</Text>
                <Text>Valor Multa</Text>
                <Text>Valor Juro</Text>
                <Text>Valor Liquido</Text>
              </VStack>
              <VStack align="end">
                <Text color={color}>
                  {formatCustomDate(
                    documento?.vencimento_fin,
                    'yyyy-MM-dd HH:mm:ss',
                    'dd/MM/yyyy',
                  ) ?? '0'}
                </Text>
                <Text color={color}>
                  {formatValue(documento?.valor_fin ?? '0')}
                </Text>
                <Text color={color}>
                  {formatValue(documento?.valormulta_fin ?? '0')}
                </Text>
                <Text color={color}>
                  {formatValue(documento?.valorjuro_fin ?? '0')}
                </Text>
                <Text color={color}>
                  {formatValue(documento?.saldo_fin ?? '0')}
                </Text>
              </VStack>
            </Flex>
            <Box rounded="2xl" mx="4" borderWidth="1px" />
            <Flex w="50%" flexDir="column">
              <Flex w="100%" flexDir="row">
                <Center pr="4">
                  <Icon as={AlertCircle} color={warningColor} boxSize="30px" />
                </Center>
                <Text color={warningColor}>
                  Pagamentos realizados via Pix a Licença será liberada
                  imediatamente após o pagamento
                </Text>
              </Flex>
              <Flex mt="4" flexDir="column">
                <VStack align="start" w="100%">
                  <Text>Forma de Pagamento</Text>
                  <Select
                    name="Forma de pagamento"
                    rounded="2xl"
                    bg={bg}
                    w="100%"
                    borderWidth="1px"
                    value={formaPagamento}
                    onChange={e => setFormaPagamento(e.target.value)}
                  >
                    <option>Pix</option>
                    <option>Boleto</option>
                  </Select>
                  <Text>Novo Vencimento</Text>
                  <Input
                    name="Novo Vencimento"
                    type="date"
                    value={novoVencimento}
                    isDisabled={formaPagamento === 'Pix'}
                    onChange={e => setNovoVencimento(e.target.value)}
                  />
                </VStack>
                <Button
                  mt="40px"
                  alignSelf="end"
                  isLoading={gerando}
                  onClick={gerarCobranca}
                >
                  Gerar
                </Button>
                {barcode !== '' && (
                  <>
                    <BarCodeModal />
                  </>
                )}
              </Flex>
            </Flex>
          </Flex>
        </Flex>
      </ModalContent>
    </Modal>
  );
};

export default InvoiceModal;
